import React from 'react';
import {
  BarChart as RechartsBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { format, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useDateFnsLocale } from '../../hooks/useDateFnsLocale';

interface BarChartProps {
  data: Array<{
    date: string;
    attendance: number;
    tasksCompleted: number;
    tasksCreated: number;
  }>;
  title?: string;
  height?: number;
  showLegend?: boolean;
}

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3">
        <p className="font-medium text-gray-900 dark:text-gray-100 mb-2">
          {label}
        </p>
        {payload.map((entry: any, index: number) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export const StatisticsBarChart: React.FC<BarChartProps> = ({
  data,
  title,
  height = 300,
  showLegend = true
}) => {
  const { t } = useTranslation();
  const dateFnsLocale = useDateFnsLocale();

  // Format data for the chart
  const formattedData = data.map(item => ({
    ...item,
    label: format(parseISO(item.date), 'MMM d', { locale: dateFnsLocale })
  }));

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">
        {t('statistics.ui.noDataAvailable')}
      </div>
    );
  }

  return (
    <div className="w-full overflow-x-auto">
      {title && (
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          {title}
        </h3>
      )}
      <ResponsiveContainer width="100%" height={height}>
        <RechartsBarChart
          data={formattedData}
          margin={{ top: 20, right: 30, left: 0, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" className="stroke-gray-200 dark:stroke-gray-700" />
          <XAxis 
            dataKey="label" 
            tick={{ fontSize: 12 }}
            className="text-gray-600 dark:text-gray-400"
          />
          <YAxis 
            tick={{ fontSize: 12 }}
            className="text-gray-600 dark:text-gray-400"
          />
          <Tooltip content={<CustomTooltip />} />
          {showLegend && (
            <Legend 
              wrapperStyle={{ fontSize: 12 }}
            />
          )}
          <Bar 
            dataKey="attendance" 
            name={t('statistics.ui.attendance')} 
            fill="#3B82F6" 
            radius={[4, 4, 0, 0]}
          />
          <Bar 
            dataKey="tasksCompleted" 
            name={t('statistics.ui.completed')} 
            fill="#10B981" 
            radius={[4, 4, 0, 0]}
          />
          <Bar 
            dataKey="tasksCreated" 
            name={t('statistics.ui.created')} 
            fill="#F59E0B" 
            radius={[4, 4, 0, 0]}
          />
        </RechartsBarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default StatisticsBarChart;
